import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import { Course } from '@e-learning/shared/src/store/types';
import { hasAttemptedQuiz } from '../../services/quizAttemptService';

interface QuizPreviewProps {
  open: boolean;
  onClose: () => void;
  onStart: () => void;
  course: Course | null;
  questionCount: number;
  isAuthenticated: boolean;
  hasAttempted?: boolean;
}

const QuizPreview: React.FC<QuizPreviewProps> = ({
  open,
  onClose,
  onStart,
  course,
  questionCount,
  isAuthenticated,
  hasAttempted = false,
}) => {
  if (!course) return null;

  const estimatedTime = Math.ceil(questionCount * 1.5); // 1.5 minutes per question
  const showFreeUserRestriction = !isAuthenticated && hasAttempted;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
          border: '1px solid rgba(148, 163, 184, 0.1)',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
            }}
          >
            <QuizIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Quiz Preview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {course.title}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <List>
          <ListItem>
            <ListItemIcon>
              <QuestionAnswerIcon sx={{ color: 'primary.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="Number of Questions"
              secondary={`${questionCount} questions`}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon>
              <AccessTimeIcon sx={{ color: 'primary.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="Estimated Time"
              secondary={`Approximately ${estimatedTime} minutes`}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon>
              <QuizIcon sx={{ color: 'primary.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="Quiz Type"
              secondary="Multiple Choice Questions"
            />
          </ListItem>
        </List>
        {showFreeUserRestriction && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={<LockIcon />}>
            <Typography variant="body2">
              <strong>Free users can only attempt each quiz once.</strong> Log in to access deeper questions and
              track your results.
            </Typography>
          </Alert>
        )}
        {!isAuthenticated && !hasAttempted && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Free users:</strong> You can attempt this quiz once. Log in to access advanced questions and
              unlimited attempts.
            </Typography>
          </Alert>
        )}
        {isAuthenticated && (
          <Box sx={{ mt: 3, p: 2, background: 'rgba(99, 102, 241, 0.1)', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> You can take this quiz multiple times. Your best score will be tracked. You have
              access to all difficulty levels.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={onStart}
          variant="contained"
          disabled={showFreeUserRestriction}
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            },
            '&:disabled': {
              background: 'rgba(148, 163, 184, 0.3)',
            },
          }}
        >
          {showFreeUserRestriction ? 'Already Attempted' : 'Start Quiz'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuizPreview;

